import { useCallback, useMemo } from 'react';
import { useApiClient } from 'hooks/useApiClient';
import { API_ROUTES } from 'utils/constants';
import {
  CreateDocumentV2Request,
  DocumentV2Kind,
  DocumentV2Record,
} from 'types/documentsV2';

function routeForKind(kind: DocumentV2Kind): string {
  switch (kind) {
    case 'sales-orders':
      return API_ROUTES.SALES_ORDER_V2;
    case 'sales':
      return API_ROUTES.SALES_V2;
    case 'purchase-orders':
      return API_ROUTES.PURCHASE_ORDER_V2;
    case 'purchases':
      return API_ROUTES.PURCHASE_V2;
    default:
      return API_ROUTES.SALES_V2;
  }
}

export function useDocumentsV2Service(kind: DocumentV2Kind) {
  const apiClient = useApiClient();
  const baseRoute = routeForKind(kind);

  const list = useCallback(
    async (includeVoided = false): Promise<DocumentV2Record[]> => {
      const query: Record<string, string> = includeVoided
        ? { includeVoided: 'true' }
        : {};
      return apiClient.get<DocumentV2Record[]>(baseRoute, query);
    },
    [apiClient, baseRoute],
  );

  const create = useCallback(
    async (payload: CreateDocumentV2Request): Promise<DocumentV2Record> => {
      const body = {
        date: payload.date,
        referenceNumber: payload.referenceNumber,
        description: payload.description,
        documentType: payload.documentType,
        currency: payload.currency,
        sellerTin: payload.sellerTin,
        sellerBranchCode: payload.sellerBranchCode,
        sellerRegisteredName: payload.sellerRegisteredName,
        sellerAddress: payload.sellerAddress,
        buyerTin: payload.buyerTin,
        buyerAddress: payload.buyerAddress,
        vatableSales: payload.vatableSales,
        vatAmount: payload.vatAmount,
        vatExemptSales: payload.vatExemptSales,
        zeroRatedSales: payload.zeroRatedSales,
        totalDiscount: payload.totalDiscount,
        lineItems: payload.lineItems.map((item) => ({
          description: item.description,
          quantity: item.quantity,
          unitOfMeasure: item.unitOfMeasure,
          discount: item.discount,
          taxType: item.taxType,
          ...(kind === 'sales-orders' || kind === 'sales'
            ? { unitPrice: item.unitPrice }
            : { unitCost: item.unitPrice }),
        })),
        ...(kind === 'sales-orders' || kind === 'sales'
          ? { buyerName: payload.counterpartyName }
          : { supplierName: payload.counterpartyName }),
        ...(kind === 'sales' && payload.sourceOrderId
          ? { sourceSalesOrderId: payload.sourceOrderId }
          : {}),
        ...(kind === 'purchases' && payload.sourceOrderId
          ? { sourcePurchaseOrderId: payload.sourceOrderId }
          : {}),
      };
      return apiClient.post<DocumentV2Record>(
        baseRoute,
        body as unknown as Record<string, unknown>,
      );
    },
    [apiClient, baseRoute, kind],
  );

  const voidRecord = useCallback(
    async (id: string, reason?: string): Promise<DocumentV2Record> => {
      const body = reason ? { reason } : undefined;
      const res = await apiClient.delete<DocumentV2Record>(
        `${baseRoute}/${id}`,
        body as unknown as Record<string, unknown>,
      );
      if (!res) throw new Error('No response when voiding record');
      return res;
    },
    [apiClient, baseRoute],
  );

  return useMemo(
    () => ({
      list,
      create,
      voidRecord,
    }),
    [list, create, voidRecord],
  );
}
